/* ===============================
   UPDATED WITH BRAND COLORS
   Primary: #3b2b9a
   Accent:  #d83b29
================================ */

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiMenu, HiX } from "react-icons/hi";
import bg from "../assets/bg.png";
import logo from "../assets/logo.png";

const TESTIMONIALS = [
    { name: "Anika Roy", text: "Found my dream villa within weeks — outstanding service!" },
    { name: "Dev Patel", text: "Smooth transaction and amazing post-sale support." },
    { name: "Sofia Mehta", text: "Premium listings and a high-touch experience." },
];

export default function App() {
    const [page, setPage] = useState("home");
    const [mobileNav, setMobileNav] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [testimonialIndex, setTestimonialIndex] = useState(0);

    useEffect(() => {
        const t = setTimeout(() => setShowContact(true), 700);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            setTestimonialIndex((i) => (i + 1) % TESTIMONIALS.length);
        }, 5000);
        return () => clearInterval(id);
    }, []);

    // Parallax
    const heroRef = useRef(null);
    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const onScroll = () => {
            const sc = window.scrollY;
            el.style.backgroundPosition = `center calc(50% + ${sc * 0.2}px)`;
        };
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-gray-200 antialiased">
            {/* NAV */}
            <header className="fixed w-full z-40">
                <nav className="backdrop-blur-md bg-white/40 shadow-md">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                            <div className="flex items-center gap-4">
                                <img src={logo} alt="Logo" className="h-12 w-auto" />

                                <div className="hidden md:flex items-center gap-6 ml-6">
                                    <button
                                        onClick={() => setPage("home")}
                                        className={`hover:text-[#d83b29] ${
                                            page === "home" ? "text-[#d83b29] font-semibold" : ""
                                        }`}
                                    >
                                        Home
                                    </button>

                                    <button
                                        onClick={() => setPage("blog")}
                                        className={`hover:text-[#d83b29] ${
                                            page === "blog" ? "text-[#d83b29] font-semibold" : ""
                                        }`}
                                    >
                                        Blog
                                    </button>

                                    <button
                                        onClick={() => setShowContact(true)}
                                        className="px-4 py-2 rounded-full bg-[#d83b29] text-white shadow hover:brightness-95"
                                    >
                                        Contact
                                    </button>
                                </div>
                            </div>

                            <div className="hidden md:flex text-sm">
                                Call us: <span className="font-semibold ml-1">+91 99999 88888</span>
                            </div>

                            {/* Mobile */}
                            <div className="md:hidden">
                                <button onClick={() => setMobileNav(!mobileNav)} className="p-2">
                                    {mobileNav ? <HiX size={24} /> : <HiMenu size={24} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Nav */}
                    <AnimatePresence>
                        {mobileNav && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="md:hidden px-6 pb-6"
                            >
                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={() => {
                                            setPage("home");
                                            setMobileNav(false);
                                        }}
                                    >
                                        Home
                                    </button>

                                    <button
                                        onClick={() => {
                                            setPage("blog");
                                            setMobileNav(false);
                                        }}
                                    >
                                        Blog
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowContact(true);
                                            setMobileNav(false);
                                        }}
                                        className="px-4 py-2 rounded-full bg-[#d83b29] text-white"
                                    >
                                        Contact
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </nav>
            </header>

            {/* MAIN */}
            <main className="pt-20">
                <AnimatePresence mode="wait">
                    {page === "home" ? (
                        <motion.section key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            {/* HERO */}
                            <section
                                ref={heroRef}
                                className="relative h-screen flex items-center"
                                style={{
                                    backgroundImage: `url(${bg})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/10 to-black/40" />

                                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                                    <div className="grid lg:grid-cols-2 gap-8 items-center py-24">
                                        <div>
                                            <motion.h1
                                                initial={{ y: 20, opacity: 0 }}
                                                animate={{ y: 0, opacity: 1 }}
                                                transition={{ delay: 0.2 }}
                                                className="text-4xl md:text-6xl font-extrabold leading-tight text-white"
                                            >
                                                Luxury living.
                                                <span className="block text-[#d83b29]">Reimagined for you.</span>
                                            </motion.h1>

                                            <motion.p
                                                initial={{ y: 20, opacity: 0 }}
                                                animate={{ y: 0, opacity: 1 }}
                                                transition={{ delay: 0.35 }}
                                                className="mt-6 text-lg text-white/90 max-w-xl"
                                            >
                                                Curated properties with premium services — discover homes that enhance your lifestyle.
                                            </motion.p>

                                            <div className="mt-8 flex items-center gap-4">
                                                <motion.button
                                                    whileHover={{ scale: 1.03 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    onClick={() => setShowContact(true)}
                                                    className="px-6 py-3 rounded-full bg-[#d83b29] text-white font-semibold shadow-lg"
                                                >
                                                    Get in touch
                                                </motion.button>

                                                <a
                                                    href="#listings"
                                                    className="px-6 py-3 rounded-full border border-white/30 text-white/90 backdrop-blur-sm"
                                                >
                                                    View listings
                                                </a>
                                            </div>

                                            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md text-white/90">
                                                <div>
                                                    <div className="text-2xl font-bold">250+</div>
                                                    <div className="text-sm">Properties</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold">35</div>
                                                    <div className="text-sm">Premium locations</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold">24/7</div>
                                                    <div className="text-sm">Concierge</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quick Search */}
                                        <motion.div
                                            initial={{ scale: 0.98, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: 0.5 }}
                                            className="bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-2xl shadow-xl text-white max-w-md"
                                        >
                                            <h3 className="text-xl font-semibold mb-4">Quick property search</h3>

                                            <form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    setShowContact(true);
                                                }}
                                                className="space-y-3"
                                            >
                                                <input
                                                    placeholder="Location (city, area)"
                                                    className="w-full px-4 py-3 rounded-lg bg-white/10 placeholder-white/70"
                                                />

                                                <div className="grid grid-cols-2 gap-3">
                                                    <select className="px-3 py-3 rounded-lg bg-white/10">
                                                        <option>Any type</option>
                                                        <option>Villa</option>
                                                        <option>Penthouse</option>
                                                        <option>Apartment</option>
                                                    </select>

                                                    <select className="px-3 py-3 rounded-lg bg-white/10">
                                                        <option>Any budget</option>
                                                        <option>₹1cr - ₹2cr</option>
                                                        <option>₹2cr - ₹5cr</option>
                                                        <option>₹5cr+</option>
                                                    </select>
                                                </div>

                                                <div className="flex gap-3">
                                                    <button className="flex-1 px-4 py-3 rounded-lg bg-[#d83b29] text-white font-semibold">
                                                        Search
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => setShowContact(true)}
                                                        className="px-4 py-3 rounded-lg border border-white/20"
                                                    >
                                                        Request Callback
                                                    </button>
                                                </div>
                                            </form>
                                        </motion.div>
                                    </div>
                                </div>
                            </section>

                            {/* LISTINGS */}
                            <section id="listings" className="py-20">
                                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                                    <div className="flex items-center justify-between mb-8">
                                        <h2 className="text-2xl font-bold text-[#3b2b9a]">Featured Listings</h2>

                                        <button onClick={() => setShowContact(true)} className="text-[#d83b29] underline">
                                            Request a viewing
                                        </button>
                                    </div>

                                    <div className="snap-x snap-mandatory overflow-x-auto flex gap-6 py-4">
                                        {Array.from({ length: 6 }).map((_, i) => (
                                            <article
                                                key={i}
                                                className="min-w-[320px] snap-center bg-white dark:bg-slate-900 rounded-2xl shadow-lg overflow-hidden"
                                            >
                                                <div
                                                    className="relative h-44"
                                                    style={{
                                                        backgroundImage:
                                                            "url('https://images.unsplash.com/photo-1570129477492-45c003edd2be?q=80&w=1200')",
                                                        backgroundSize: "cover",
                                                        backgroundPosition: "center",
                                                    }}
                                                />

                                                <div className="p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h3 className="font-semibold">Modern Villa in Goa</h3>
                                                            <div className="text-sm text-gray-500">
                                                                4 bed • 3 bath • 3500 sqft
                                                            </div>
                                                        </div>

                                                        <div className="text-[#d83b29] font-bold">₹4.2cr</div>
                                                    </div>

                                                    <div className="mt-3 flex items-center justify-between">
                                                        <div className="text-sm text-gray-500">
                                                            Private pool • Sea view
                                                        </div>

                                                        <button
                                                            onClick={() => setShowContact(true)}
                                                            className="px-3 py-2 rounded-md bg-[#3b2b9a] text-white"
                                                        >
                                                            Enquire
                                                        </button>
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            </section>

                            {/* FEATURES */}
                            <section className="py-20 bg-white dark:bg-slate-900">
                                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                                    <div className="grid lg:grid-cols-3 gap-8">
                                        <div className="p-6 rounded-xl shadow border-l-4 border-[#3b2b9a]">
                                            <h4 className="font-semibold text-[#3b2b9a]">Curated Listings</h4>
                                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                Handpicked properties vetted by our experts.
                                            </p>
                                        </div>

                                        <div className="p-6 rounded-xl shadow border-l-4 border-[#3b2b9a]">
                                            <h4 className="font-semibold text-[#3b2b9a]">End-to-end Support</h4>
                                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                Legal, finance and interior services covered.
                                            </p>
                                        </div>

                                        <div className="p-6 rounded-xl shadow border-l-4 border-[#3b2b9a]">
                                            <h4 className="font-semibold text-[#3b2b9a]">Premium Service</h4>
                                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                Concierge and after-sales assistance.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* TESTIMONIALS */}
                            <section className="py-16">
                                <div className="max-w-4xl mx-auto px-6 text-center">
                                    <h3 className="text-2xl font-bold text-[#3b2b9a] mb-6">What our clients say</h3>

                                    <motion.div
                                        key={testimonialIndex}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="bg-white dark:bg-slate-900 rounded-xl shadow p-8"
                                    >
                                        <p className="italic">“{TESTIMONIALS[testimonialIndex].text}”</p>
                                        <div className="mt-4 font-semibold text-[#d83b29]">
                                            — {TESTIMONIALS[testimonialIndex].name}
                                        </div>
                                    </motion.div>
                                </div>
                            </section>

                            {/* FOOTER */}
                            <footer className="bg-[#3b2b9a] text-white py-12 mt-12">
                                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
                                    <div>
                                        <div className="text-xl font-bold">HavanaEstates</div>
                                        <p className="mt-4 text-sm text-white/80">
                                            Premium properties, exceptional service.
                                        </p>
                                    </div>

                                    <div>
                                        <h4 className="font-semibold">Quick links</h4>
                                        <ul className="mt-3 text-sm text-white/80 space-y-2">
                                            <li onClick={() => setPage("home")} className="cursor-pointer">
                                                Home
                                            </li>
                                            <li onClick={() => setPage("blog")} className="cursor-pointer">
                                                Blog
                                            </li>
                                        </ul>
                                    </div>

                                    <div>
                                        <h4 className="font-semibold">Contact</h4>
                                        <div className="mt-3 text-sm text-white/80">
                                            +91 99999 88888<br />hello@havanaestates.com
                                        </div>
                                    </div>
                                </div>
                            </footer>
                        </motion.section>
                    ) : (
                        /* BLOG PAGE */
                        <motion.section key="blog" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen py-20 bg-gray-50 dark:bg-slate-950">
                            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                                <div className="flex items-center justify-between mb-8">
                                    <h2 className="text-3xl font-bold text-[#3b2b9a]">Insights & Stories</h2>
                                    <button onClick={() => setPage('home')} className="px-4 py-2 rounded-full border border-[#3b2b9a] text-[#3b2b9a]">
                                        Back to home
                                    </button>
                                </div>

                                <div className="grid md:grid-cols-2 gap-8">
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <article key={i} className="bg-white dark:bg-slate-900 rounded-2xl shadow overflow-hidden">
                                            <div className="h-44" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1505691723518-36a5b3a5d1d2?q=80')`, backgroundSize: "cover", backgroundPosition: "center" }} />
                                            <div className="p-6">
                                                <h3 className="font-semibold text-lg text-[#3b2b9a]">
                                                    Market trends every investor should watch
                                                </h3>
                                                <p className="mt-2 text-sm text-gray-500">
                                                    Quick guide to 2025 high ROI locations.
                                                </p>

                                                <div className="mt-4 flex items-center justify-between">
                                                    <div className="text-sm text-gray-500">Nov 10, 2025</div>
                                                    <button
                                                        onClick={() => setShowContact(true)}
                                                        className="text-[#d83b29] font-semibold"
                                                    >
                                                        Contact to learn more
                                                    </button>
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>
            </main>

            {/* CONTACT MODAL */}
            <AnimatePresence>
                {showContact && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center">
                        <div className="absolute inset-0 bg-black/50" onClick={() => setShowContact(false)} />

                        <motion.div initial={{ y: 20, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, opacity: 0 }} className="relative max-w-xl w-full mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-xl font-bold text-[#3b2b9a]">Let's get in touch</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Our expert will call you within 24 hours.</p>
                                </div>
                                <button onClick={() => setShowContact(false)} className="text-gray-400 hover:text-gray-600 dark:text-gray-400">✕</button>
                            </div>

                            {!submitted ? (
                                <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }} className="mt-4 space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input required placeholder="Full name" className="px-4 py-3 border rounded-lg" />
                                        <input required type="email" placeholder="Email" className="px-4 py-3 border rounded-lg" />
                                    </div>

                                    <input required placeholder="Phone" className="px-4 py-3 border rounded-lg w-full" />
                                    <textarea placeholder="Message (optional)" className="w-full px-4 py-3 border rounded-lg" rows={4} />

                                    <div className="flex items-center justify-between mt-2">
                                        <div className="text-sm text-gray-500">
                                            Or call us:
                                            <span className="font-semibold text-gray-900 dark:text-gray-100 ml-1">+91 99999 88888</span>
                                        </div>

                                        <button type="submit" className="px-5 py-3 rounded-full bg-[#d83b29] text-white">
                                            Send enquiry
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="mt-6 text-center">
                                    <div className="text-lg font-semibold text-[#3b2b9a]">
                                        Thank you! Our team will reach out shortly.
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
